/**
 * @mentions in comments (docs/15-zusammenarbeit.md). A mention is stored in the
 * comment's text as a token `@[Name](user:<id>)`: it survives edits, needs no
 * extra column, and turns into readable `@Name` wherever plain text is needed
 * (mails, MCP, guests of a share link).
 */

export interface Mention {
  id: string;
  name: string;
}

export type MentionPart = { text: string } | { mention: Mention };

const MENTION_SOURCE = String.raw`@\[([^\]\n]{1,120})\]\(user:([^)\s]{1,100})\)`;

const mentionPattern = () => new RegExp(MENTION_SOURCE, "g");

/** A name as it may appear inside a token (no brackets, no line breaks). */
function tokenName(name: string): string {
  return (
    name
      .replace(/[[\]\n\r]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 120) || "Unbekannt"
  );
}

export function formatMention(user: Mention): string {
  return `@[${tokenName(user.name)}](user:${user.id})`;
}

/** Ids of the users mentioned in a text (each once, in order of appearance). */
export function mentionedUserIds(body: string): string[] {
  const ids = new Set<string>();
  for (const match of body.matchAll(mentionPattern())) ids.add(match[2]!);
  return [...ids];
}

/** The text with mentions as `@Name` (mails, MCP, guests). */
export function mentionsToPlainText(body: string): string {
  return body.replace(mentionPattern(), (_, name: string) => `@${name}`);
}

/**
 * Keeps only mentions for which `keep` returns true; the others become plain
 * `@Name` (e.g. users who are not members, or anything a guest typed).
 */
export function filterMentions(body: string, keep: (id: string) => boolean): string {
  return body.replace(mentionPattern(), (token, name: string, id: string) =>
    keep(id) ? token : `@${name}`,
  );
}

/** Text and mentions in order, for rendering. */
export function splitMentions(body: string): MentionPart[] {
  const parts: MentionPart[] = [];
  let last = 0;
  for (const match of body.matchAll(mentionPattern())) {
    if (match.index > last) parts.push({ text: body.slice(last, match.index) });
    parts.push({ mention: { name: match[1]!, id: match[2]! } });
    last = match.index + match[0].length;
  }
  if (last < body.length) parts.push({ text: body.slice(last) });
  return parts;
}

/** Stored text → what the comment field shows (`@Name`) plus the mentions in it. */
export function decodeMentions(body: string): { text: string; mentions: Mention[] } {
  const mentions = new Map<string, Mention>();
  for (const match of body.matchAll(mentionPattern())) {
    mentions.set(match[2]!, { id: match[2]!, name: match[1]! });
  }
  return { text: mentionsToPlainText(body), mentions: [...mentions.values()] };
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The comment field's text (`@Name`) → stored text: every `@Name` of a picked
 * mention becomes a token. Longer names first, so "@Anna Maria" wins over "@Anna";
 * a name only counts when it is not followed by more letters.
 */
export function encodeMentions(text: string, mentions: readonly Mention[]): string {
  const sorted = [...mentions].sort((a, b) => tokenName(b.name).length - tokenName(a.name).length);
  if (sorted.length === 0) return text;
  const byName = new Map(sorted.map((mention) => [tokenName(mention.name), mention]));
  const pattern = new RegExp(
    `@(${sorted.map((mention) => escapeRegExp(tokenName(mention.name))).join("|")})(?![\\p{L}\\p{N}_])`,
    "gu",
  );
  return text.replace(pattern, (token, name: string) => {
    const mention = byName.get(name);
    return mention ? formatMention(mention) : token;
  });
}

/**
 * The `@query` right before the caret, if the user is typing a mention:
 * `@` at the start or after a space, then up to 40 characters without a line break.
 */
export function mentionQueryAt(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = /(^|\s)@([^\n@]{0,40})$/u.exec(before);
  if (!match) return null;
  const query = match[2]!;
  // Two spaces in a row end it (a sentence went on).
  if (/\s{2}/.test(query)) return null;
  return { start: before.length - query.length - 1, query };
}
